import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { clsx } from 'clsx';
import useUpcomingNotifications from '../../hooks/useUpcomingNotifications.jsx';
import Notifications from '../Notifications';
import { createClient } from '@supabase/supabase-js';
import { playNotificationSound, unlockAudio } from '../../utils/notificationSounds';
import {
  HomeIcon,
  DocumentTextIcon,
  PlusIcon,
  UsersIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CogIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ClockIcon,
  CalendarIcon,
  ChartBarIcon,
  TruckIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  BeakerIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  QrCodeIcon,
  FingerPrintIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Göstergeler', href: '/dashboard', icon: HomeIcon },
  { name: 'Vakalar', href: '/cases', icon: DocumentTextIcon },
  { name: 'Mobil Vakalar', href: '/mobile-cases', icon: DevicePhoneMobileIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator'] },
  { name: 'Planlı Vakalar', href: '/cases/planned', icon: ClockIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'paramedic', 'external_presidency', 'external_hospital', 'external_parent_institution'] },
  { name: 'Yeni Vaka', href: '/cases/new', icon: PlusIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'external_presidency'] },
  { name: 'Organizasyonlar', href: '/organizations', icon: BuildingOfficeIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'paramedic'] },
  { name: 'Kurumlar', href: '/pricing', icon: BuildingLibraryIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'external_institution', 'external_parent_institution'] },
  { name: 'Kullanıcılar', href: '/users', icon: UsersIcon, roles: ['super_admin', 'admin', 'supervisor', 'external_institution'] },
  { name: 'Toplu Kullanıcı Ekle', href: '/users/bulk-mobile', icon: UsersIcon, roles: ['external_institution'] },
  { name: 'Onay Bekleyenler', href: '/users/pending-approval', icon: ClockIcon, roles: ['super_admin', 'admin'] },
  { name: 'Personeller', href: '/personnel', icon: UsersIcon, roles: ['super_admin', 'admin', 'supervisor', 'ik'] },
  { name: 'Nöbet & Puantaj', href: '/personnel/puantaj', icon: CalendarIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'ik'] },
  { name: 'Giriş-Çıkış', href: '/checkin', icon: FingerPrintIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'ik', 'paramedic', 'surucu', 'saha_personeli', 'muhasebe', 'lojistik', 'kurumsal_pazarlama', 'destek_personeli'] },
  { name: 'Puantajım', href: '/puantajim', icon: ClockIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'ik', 'paramedic', 'surucu', 'saha_personeli', 'muhasebe', 'lojistik', 'kurumsal_pazarlama', 'destek_personeli'] },
  { name: 'QR Yoklama', href: '/attendance/kiosk', icon: QrCodeIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'ik'] },
  { name: 'Yoklama Takibi', href: '/attendance', icon: ClipboardDocumentCheckIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator', 'ik'] },
  { name: 'Ekipler', href: '/teams', icon: UserGroupIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader'] },
  { name: 'Günlük Atamalar', href: '/teams/daily', icon: CalendarIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator'] },
  { name: 'Ekip Durumu', href: '/teams/status', icon: ChartBarIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader', 'operator'] },
  { name: 'Hastaneler', href: '/hospitals', icon: TruckIcon, roles: ['super_admin', 'admin', 'external_presidency', 'external_hospital'] },
  { name: 'Tıbbi Tanımlar', href: '/medical-definitions', icon: BeakerIcon, roles: ['super_admin', 'admin', 'supervisor', 'team_leader'] },
  { name: 'Adresler/Sevk Türleri', href: '/lookup-definitions', icon: MapPinIcon, roles: ['super_admin', 'admin', 'supervisor'] },
  { name: 'Teklifler', href: '/quotes', icon: CurrencyDollarIcon, roles: ['super_admin', 'admin', 'supervisor'] },
  { name: 'Hizmet Raporu', href: '/reports', icon: ChartBarIcon, roles: ['super_admin', 'admin', 'supervisor', 'external_presidency', 'external_hospital', 'external_parent_institution'] },
];

const userNavigation = [
  { name: 'Profil', href: '/profile', icon: UserIcon },
  { name: 'Ayarlar', href: '/settings', icon: CogIcon },
];

// Supabase client for realtime
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseAnonKey ? createClient(supabaseUrl, supabaseAnonKey) : null;

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, startSessionTimeout, clearSessionTimeout } = useAuthStore();
  const queryClient = useQueryClient();
  
  // Enable upcoming notifications
  useUpcomingNotifications();

  // AudioContext'i kullanıcı etkileşiminde unlock et (tarayıcı autoplay politikası)
  useEffect(() => {
    document.addEventListener('click', unlockAudio, { passive: true });
    document.addEventListener('keydown', unlockAudio, { passive: true });
    document.addEventListener('touchstart', unlockAudio, { passive: true });
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Bekleyen mobil vaka sayısını çek
  const { data: pendingMobileCaseCount = 0 } = useQuery({
    queryKey: ['pending-mobile-cases-count'],
    queryFn: async () => {
      const response = await api.get('/mobile-cases?status=pending_approval&limit=1');
      return response.data?.pagination?.total ?? response.data?.data?.pagination?.total ?? 0;
    },
    refetchInterval: 5000,
    enabled: !!user && !['external_institution', 'mobile_user', 'paramedic', 'external_hospital'].includes(user?.role),
  });

  // Bildirim okunmamış sayısı (badge'i gizlemek için)
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await api.get('/notifications');
      return res.data?.data ?? { notifications: [], unread_count: 0 };
    },
    enabled: !!user,
  });
  const unreadNotificationCount = notificationsData?.unread_count ?? 0;

  // Mobil Vakalar sayfasına girip çıkınca veya bildirimler okununca badge gizlensin
  useEffect(() => {
    if (location.pathname === '/mobile-cases') {
      sessionStorage.setItem('mobileCasesPageVisitedCount', String(pendingMobileCaseCount));
    }
  }, [location.pathname, pendingMobileCaseCount]);

  const lastVisitedCount = parseInt(sessionStorage.getItem('mobileCasesPageVisitedCount') || '0', 10);
  const showMobileCasesBadge =
    pendingMobileCaseCount > 0 &&
    (unreadNotificationCount > 0 || pendingMobileCaseCount > lastVisitedCount);

  // Supabase Realtime subscription for mobile_cases
  useEffect(() => {
    if (!supabase) {
      console.log('⚠️ Supabase client not initialized - check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
      return;
    }
    
    if (!user || ['external_institution', 'mobile_user', 'paramedic', 'external_hospital'].includes(user?.role)) {
      return;
    }

    console.log('🔌 Connecting to Supabase Realtime for mobile_cases...');
    
    const channel = supabase
      .channel('mobile-cases-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mobile_cases'
        },
        (payload) => {
          console.log('🔔 New mobile case detected via Realtime:', payload);
          // Invalidate the query to refresh the count
          queryClient.invalidateQueries(['pending-mobile-cases-count']);
          queryClient.invalidateQueries(['notifications']);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'mobile_cases'
        },
        (payload) => {
          console.log('🔄 Mobile case updated via Realtime:', payload);
          queryClient.invalidateQueries(['pending-mobile-cases-count']);
        }
      )
      .subscribe((status) => {
        console.log('📡 Mobile cases Realtime status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to mobile_cases realtime changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Failed to subscribe to mobile_cases - check Supabase Realtime settings');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  // Supabase Realtime subscription for notifications
  useEffect(() => {
    if (!supabase || !user) {
      return;
    }

    console.log('🔌 Connecting to Supabase Realtime for notifications (user_id:', user.id, ')...');
    
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 New notification via Realtime:', payload);
          playNotificationSound(payload.new?.type);
          queryClient.invalidateQueries(['notifications']);
        }
      )
      .subscribe((status) => {
        console.log('📡 Notifications Realtime status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to notifications realtime changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Failed to subscribe to notifications - check Supabase Realtime settings');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  // Get session timeout based on user role
  const getSessionTimeout = (userRole) => {
    if (userRole === 'paramedic') {
      return 24 * 60 * 60 * 1000; // 24 hours for paramedics
    }
    return 2 * 60 * 60 * 1000; // 2 hours for other users
  };

  // Calculate and update remaining session time
  useEffect(() => {
    if (!user) return;

    const SESSION_TIMEOUT = getSessionTimeout(user.role);
    let loggedOut = false;
    
    const updateRemainingTime = () => {
      const loginTimeKey = 'session_login_time';
      const storedLoginTime = localStorage.getItem(loginTimeKey);
      
      if (!storedLoginTime) {
        setRemainingTime('');
        setShowCountdown(false);
        return;
      }
      
      const loginTime = parseInt(storedLoginTime, 10);
      const elapsedTime = Date.now() - loginTime;
      const remaining = SESSION_TIMEOUT - elapsedTime;
      const remainingSeconds = Math.floor(remaining / 1000);
      
      if (remaining <= 0) {
        setRemainingTime('00:00');
        setShowCountdown(false);
        // Güvenlik: zaman gerçekten sıfırladıysa oturumu kapat
        if (!loggedOut) {
          loggedOut = true;
          localStorage.removeItem(loginTimeKey);
          localStorage.setItem('session_timeout', 'true');
          logout().then(() => { window.location.href = '/login'; });
        }
        return;
      }
      
      // Show countdown modal in last 30 seconds only
      if (remainingSeconds <= 30 && remainingSeconds > 0) {
        setShowCountdown(true);
        setCountdown(remainingSeconds);
      } else {
        setShowCountdown(false);
      }
      
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      
      if (hours > 0) {
        setRemainingTime(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setRemainingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };
    
    // Update immediately
    updateRemainingTime();
    
    // Update every second
    const interval = setInterval(updateRemainingTime, 1000);
    
    return () => clearInterval(interval);
  }, [user, logout]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isCurrentPath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => {
    const userRole = user?.role;
    
    // Check if item has specific roles requirement
    if (item.roles && !item.roles.includes(userRole)) {
      return false;
    }
    
    // Super Admin can access everything
    if (userRole === 'super_admin') {
      return !item.roles || item.roles.includes('super_admin') || item.roles.includes('admin');
    }
    
    // Admin can access everything (except role-specific items that don't include admin)
    if (userRole === 'admin') {
      return !item.roles || item.roles.includes('admin');
    }
    
    // Supervisor and Team Leader access
    if (userRole === 'supervisor' || userRole === 'team_leader') {
      // Exclude restricted pages
      const restrictedPages = ['/hospitals'];
      return !restrictedPages.includes(item.href) && (!item.roles || item.roles.includes(userRole));
    }
    
    // Operator and Paramedic access
    if (userRole === 'operator' || userRole === 'paramedic') {
      const allowedPages = userRole === 'paramedic'
        ? ['/dashboard', '/cases', '/cases/planned', '/organizations', '/checkin', '/puantajim']
        : ['/dashboard', '/cases', '/mobile-cases', '/cases/planned', '/cases/new', '/organizations', '/teams/status', '/teams/daily', '/checkin', '/attendance', '/attendance/kiosk', '/personnel/puantaj', '/puantajim'];
      return allowedPages.includes(item.href);
    }
    
    // External Institution access - dashboard, cases, users, bulk-mobile, pricing (sadece mutabakat)
    if (userRole === 'external_institution') {
      const allowedPages = ['/dashboard', '/cases', '/users', '/users/bulk-mobile', '/pricing'];
      const excludedPages = ['/cases/new', '/cases/planned', '/mobile-cases', '/organizations', '/teams', '/hospitals', '/pricing/zones'];
      if (excludedPages.includes(item.href)) {
        return false;
      }
      return allowedPages.includes(item.href) && (!item.roles || item.roles.includes('external_institution'));
    }

    // Çatı Kurum: Göstergeler, Vakalar, Planlı Vakalar, Kurumlar, Hizmet Raporu
    if (userRole === 'external_parent_institution') {
      const allowedPages = ['/dashboard', '/cases', '/cases/planned', '/pricing', '/reports'];
      return allowedPages.includes(item.href) && (!item.roles || item.roles.includes('external_parent_institution'));
    }

    // 112 Başkanlığı dış kullanıcısı: Göstergeler, Vakalar, Yeni Vaka, Hizmet Raporu, Hastaneler
    if (userRole === 'external_presidency') {
      const allowedPages = ['/dashboard', '/cases', '/cases/new', '/cases/planned', '/reports', '/hospitals'];
      return allowedPages.includes(item.href) && (!item.roles || item.roles.includes('external_presidency'));
    }

    // 112 Hastane Dış Kullanıcısı: Göstergeler, Vakalar, Planlı Vakalar, Hastaneler, Hizmet Raporu
    if (userRole === 'external_hospital') {
      const allowedPages = ['/dashboard', '/cases', '/cases/planned', '/hospitals', '/reports'];
      return allowedPages.includes(item.href) && (!item.roles || item.roles.includes('external_hospital'));
    }
    
    // İK personeli: Göstergeler + Personeller + yoklama + puantaj
    if (userRole === 'ik') {
      const allowedPages = ['/dashboard', '/personnel', '/personnel/puantaj', '/checkin', '/attendance', '/attendance/kiosk', '/puantajim'];
      return allowedPages.includes(item.href);
    }

    // Diğer idari personel rolleri: Göstergeler + kendi giriş-çıkışı + puantajı
    if (['muhasebe', 'lojistik', 'kurumsal_pazarlama', 'destek_personeli'].includes(userRole)) {
      const allowedPages = ['/dashboard', '/checkin', '/puantajim'];
      return allowedPages.includes(item.href);
    }

    // Saha personeli (sürücü, saha personeli): Göstergeler + kendi giriş-çıkışı + puantajı
    if (['surucu', 'saha_personeli'].includes(userRole)) {
      const allowedPages = ['/dashboard', '/checkin', '/puantajim'];
      return allowedPages.includes(item.href);
    }

    // Default: no access
    return false;
  });

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 flex z-40 md:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          
          <SidebarContent 
            navigation={filteredNavigation}
            userNavigation={userNavigation}
            isCurrentPath={isCurrentPath}
            user={user}
            onLogout={handleLogout}
            pendingMobileCaseCount={pendingMobileCaseCount}
            showMobileCasesBadge={showMobileCasesBadge}
          />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent 
            navigation={filteredNavigation}
            userNavigation={userNavigation}
            isCurrentPath={isCurrentPath}
            user={user}
            onLogout={handleLogout}
            pendingMobileCaseCount={pendingMobileCaseCount}
            showMobileCasesBadge={showMobileCasesBadge}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top nav */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1 flex">
              <h1 className="text-xl font-semibold text-gray-900">
                EMT Ambulans CRM
              </h1>
            </div>
            
            <div className="ml-4 flex items-center md:ml-6">
              <Notifications />
              <span className="text-sm text-gray-700 mr-4 ml-2">
                {(user?.first_name || user?.firstName || '')} {(user?.last_name || user?.lastName || '')}
              </span>
              {remainingTime && (
                <span className={`text-xs font-medium mr-3 flex items-center ${showCountdown ? 'text-red-600 animate-pulse' : 'text-orange-600'}`} title="Oturum için kalan süre">
                  <ClockIcon className="h-3 w-3 mr-1" />
                  {remainingTime}
                </span>
              )}
              <span className="text-xs text-gray-500 mr-4">
                {user?.department?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Countdown Modal (last 10 seconds) */}
      {showCountdown && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl p-8 text-center shadow-2xl max-w-sm mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Oturum Sonlanıyor</h2>
            
            {/* Dönen Sayaç */}
            <div className="relative w-32 h-32 mx-auto mb-6">
              {/* Arka plan daire */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                {/* Animasyonlu progress daire */}
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#ef4444"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(countdown / 30) * 352} 352`}
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              {/* Sayaç numarası */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-5xl font-bold text-red-600">{countdown}</span>
              </div>
            </div>
            
            <p className="text-gray-500 mb-6 text-sm">Saniye içinde oturumunuz kapanacak</p>
            
            {/* Butonlar */}
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  await logout();
                  navigate('/login');
                }}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Oturumu Sonlandır
              </button>
              <button
                onClick={() => {
                  // Önce mevcut timer'ı temizle (race condition önleme)
                  clearSessionTimeout();
                  // Oturum başlangıç zamanını sıfırla
                  localStorage.setItem('session_login_time', Date.now().toString());
                  // Yeni timer'ı başlat
                  startSessionTimeout();
                  setShowCountdown(false);
                  setCountdown(30);
                }}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ navigation, userNavigation, isCurrentPath, user, onLogout, pendingMobileCaseCount = 0, showMobileCasesBadge = false }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center h-16 flex-shrink-0 px-4 bg-medical-600">
        <img
          className="h-8 w-auto"
          src="/logo.svg"
                          alt="EMT Ambulans CRM"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <span className="hidden ml-3 text-white text-lg font-semibold">
                      EMT Ambulans
        </span>
      </div>

      {/* Navigation */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-2 py-4 bg-white space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const current = isCurrentPath(item.href);
            const isMobileCases = item.href === '/mobile-cases';
            const showBadge = isMobileCases && showMobileCasesBadge;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  current
                    ? 'bg-medical-100 border-medical-500 text-medical-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  'group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-150 relative'
                )}
              >
                <div className="relative">
                  <Icon
                    className={clsx(
                      current ? 'text-medical-500' : 'text-gray-400 group-hover:text-gray-500',
                      'mr-3 h-5 w-5'
                    )}
                  />
                </div>
                <span className="flex-1">{item.name}</span>
                {/* Mobil vaka sayısı badge - sayfa ziyaret edilip bildirimler okunmuşsa gizlenir */}
                {showBadge && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                    {pendingMobileCaseCount > 9 ? '9+' : pendingMobileCaseCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
          <div className="flex-shrink-0 w-full group block">
            <div className="flex items-center">
              <div className="inline-block h-10 w-10 rounded-full bg-medical-500 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {(user?.first_name || user?.firstName || '?').charAt(0)}
                  {(user?.last_name || user?.lastName || '').charAt(0)}
                </span>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {(user?.first_name || user?.firstName || '')} {(user?.last_name || user?.lastName || '')}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.department?.name || ''}
                </p>
              </div>
            </div>
            
            <div className="mt-3 space-y-1">
              {userNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="group flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  >
                    <Icon className="mr-3 h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
              
              <button
                onClick={onLogout}
                className="w-full group flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 